import { SiteDown } from "components/AppendixScreen/SiteDown";
import React, { ComponentType, PropsWithChildren, ReactNode } from "react";
import { Spinner } from "components/common/Spinner/Spinner";
import { NullableField } from "./extendedTypes";
import { ErrorProps } from "components/common/Notification/ErrorNotification";
export type HasChildren = { children?: ReactNode };
export type FetchProps = {
  fetching: boolean;
  loadingTitle?: string;
  loadingSubTitle?: string;
};
// export type ErrorProps = {
//   error: Nullable<ApplicationError>;
// };

type ComponentEnhancer<TInner, TOuter> = (
  component: ComponentType<TInner>
) => ComponentType<TOuter>;

const RenderNothing = () => null;

export const SpinnerIfLoading = <
  TInner,
  TOuter extends TInner & FetchProps = TInner & FetchProps,
>(
  isLoading: (props: TOuter) => boolean
): ComponentEnhancer<TInner, TOuter> => {
  return (Component: ComponentType<TInner>) => {
    return (props: TOuter) => {
      if (isLoading(props)) {
        return <Spinner />;
      } else {
        const componentPropsUnknown = props as PropsWithChildren<unknown>;
        const componentProps =
          componentPropsUnknown as PropsWithChildren<TInner>;
        return <Component {...componentProps} />;
      }
    };
  };
};

export const HideIfNoData = <TInner, TOuter = TInner>(
  hasNoData: (props: TOuter) => boolean
): ComponentEnhancer<TInner, TOuter> => {
  return (Component: ComponentType<TInner>) => {
    return (props: TOuter) => {
      if (hasNoData(props)) {
        return <RenderNothing />;
      } else {
        const componentPropsUnknown = props as PropsWithChildren<unknown>;
        const componentProps =
          componentPropsUnknown as PropsWithChildren<TInner>;
        return <Component {...componentProps} />;
      }
    };
  };
};

export const ErrorNotification = <
  TInner,
  TOuter extends TInner & ErrorProps = TInner & ErrorProps,
>(
  errorState: (props: TOuter) => boolean,
  ErrorComponent = SiteDown
): ComponentEnhancer<TInner, TOuter> => {
  return (Component: ComponentType<TInner>) => {
    return (props: TOuter) => {
      if (errorState(props)) {
        return <ErrorComponent message={props.error?.description ?? null} />;
      } else {
        const componentPropsUnknown = props as PropsWithChildren<unknown>;
        const componentProps =
          componentPropsUnknown as PropsWithChildren<TInner>;
        return <Component {...componentProps} />;
      }
    };
  };
};

const composeEnhancers = <TInner, TOuter>(
  ...enhancers: ComponentEnhancer<any, any>[]
): ComponentEnhancer<TInner, TOuter> => {
  return (Component: ComponentType<TInner>) => {
    return enhancers.reduceRight(
      (acc, enhancer) => enhancer(acc),
      Component as ComponentType<any>
    );
  };
};

export function CreateEnhancer<
  TOrigProps,
  TNullableFields extends keyof TOrigProps,
>(
  hidePredicate: (p: NullableField<TOrigProps, TNullableFields>) => boolean,
  ErrorComponent = SiteDown
): ComponentEnhancer<
  TOrigProps,
  FetchProps & ErrorProps & NullableField<TOrigProps, TNullableFields>
> {
  return composeEnhancers(
    SpinnerIfLoading<TOrigProps>((props: FetchProps) => props.fetching),
    ErrorNotification<TOrigProps>(
      (props: ErrorProps) => !!props.error,
      ErrorComponent
    ),
    HideIfNoData<TOrigProps, NullableField<TOrigProps, TNullableFields>>(
      hidePredicate
    )
  );
}

export const ShowWhen = <TInner, TOuter = TInner>(
  when: (props: TOuter) => boolean,
  Fallback: ComponentType<any> = () => null
): ComponentEnhancer<TInner, TOuter> => {
  return (Component: ComponentType<TInner>) => {
    return (props: TOuter) => {
      if (when(props)) {
        const componentPropsUnknown = props as PropsWithChildren<unknown>;
        const componentProps =
          componentPropsUnknown as PropsWithChildren<TInner>;
        return <Component {...componentProps} />;
      } else {
        return <Fallback />;
      }
    };
  };
};

export const Visible = <TProps,>(Component: React.ComponentType<TProps>) =>
  ShowWhen<TProps, TProps & { visible: boolean }>((p) => p.visible)(Component);
